import { SoundName } from 'features/audio-player/enums';
import { Sound } from 'features/audio-player/services';

/**
 * Tracks the lifecycle state of a sound.
 */
interface SoundInfo {
  state: 'PLAYING' | 'STOPPED';
  expectedEndTime?: number;
  name: SoundName;
}

export class AudioPlayer {
  private static readonly soundMap = new Map<Sound, SoundInfo>();

  private static _ringVolume = 1;

  private static readonly ringSounds = new Set<SoundName>([
    SoundName.RINGING,
    SoundName.HEARING_RINGING,
    SoundName.EMERGENCY_RINGING,
    SoundName.HEARING_TO_DEAF_RINGING,
    SoundName.DEAF_TO_HEARING_RINGING,
  ]);

  public static set ringVolume(percent: number) {
    const clamped = Math.min(Math.max(percent, 0), 100);
    AudioPlayer._ringVolume = clamped / 100;
  }

  /**
   * Check if a sound with the given name is already playing
   */
  private static isSoundPlaying(name: SoundName): boolean {
    for (const info of this.soundMap.values()) {
      if (info.name === name && info.state === 'PLAYING') {
        return true;
      }
    }
    return false;
  }

  /**
   * Play a sound by name.
   * @returns true if the sound started playing, false if it was already playing
   */
  static async play(name: SoundName): Promise<boolean> {
    // Always clean up before adding new sounds
    this.cleanupExpiredSamples();

    // Don't play the same sound multiple times
    if (this.isSoundPlaying(name)) {
      return false;
    }

    const sound = Sound.create(name);
    this.soundMap.set(sound, {
      state: 'PLAYING',
      name,
    });

    if (this.ringSounds.has(name)) {
      sound.volume = this._ringVolume;
    }

    await sound.play();
    return true;
  }

  /**
   * Play a short sample of a sound.
   * @returns true if the sound started playing, false if it was already playing
   */
  static playSample(name: SoundName, duration?: number): boolean {
    // Always clean up before adding new sounds
    this.cleanupExpiredSamples();
    if (this.isSoundPlaying(name)) {
      return false;
    }

    const sound = Sound.create(name);

    const sampleDuration = duration ?? 3000;
    const expectedEndTime = Date.now() + sampleDuration;

    this.soundMap.set(sound, {
      state: 'PLAYING',
      expectedEndTime,
      name,
    });

    sound.volume = this._ringVolume;
    sound.playSample(duration);
    return true;
  }

  /**
   * Stop all currently playing sounds.
   */
  static stop(): void {
    for (const [sound, info] of this.soundMap) {
      if (info.state !== 'PLAYING') {
        continue;
      }

      // mark as stopped and halt playback
      info.state = 'STOPPED';
      try {
        sound.stop();
      } catch (e) {
        console.error('Error stopping sound:', e);
      }
    }

    this.cleanup();
  }

  /**
   * Stop a specific sound by name.
   * @returns true if a sound was stopped, false if no matching sound was found
   */
  static stopSound(name: SoundName): boolean {
    let stopped = false;

    for (const [sound, info] of this.soundMap) {
      if (info.name === name && info.state === 'PLAYING') {
        info.state = 'STOPPED';
        try {
          sound.stop();
          stopped = true;
        } catch (e) {
          console.error('Error stopping sound:', e);
        }
      }
    }

    this.cleanup();
    return stopped;
  }

  /**
   * Clean up any samples that should have finished based on their expected end time
   */
  private static cleanupExpiredSamples(): void {
    const now = Date.now();

    for (const [_sound, info] of this.soundMap) {
      // If it has an expected end time and that time has passed, mark it as stopped
      if (info.expectedEndTime && now >= info.expectedEndTime) {
        info.state = 'STOPPED';
      }
    }

    this.cleanup();
  }

  /**
   * General cleanup of stopped sounds
   */
  private static cleanup(): void {
    for (const [sound, info] of this.soundMap) {
      if (info.state === 'STOPPED') {
        this.soundMap.delete(sound);
      }
    }
  }
}
